/**
 * PartiesRelatedTab - Contact parties (2x2 grid) + Related Tasks panel.
 *
 * Left (~60%): 4 contact cards (Ordering Party, Bill To, Deliver To, Shipper)
 * Right (~40%): Related Tasks — only tasks from the same order
 */

import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  User,
  DollarSign,
  MapPin,
  Building2,
  Phone,
  Mail,
  Navigation,
  FileText,
  Calendar,
  Package,
  ExternalLink,
  CheckCircle2,
} from 'lucide-react'
import type { ContainerTask, PartyType } from '../../../types/container'
import { taskStatusConfig } from '../../../data/dispatchData'
import { formatLfdMedium } from '../../../utils/lfdDate'
import { completedMarker } from '../../../utils/apptSchedule'
import taskAPI from '../../../services/taskApi'
import { backendTaskToContainer } from '../../../utils/backendTaskToContainer'

// ── Party type configuration ──
const PARTY_TYPE_CONFIG: Record<PartyType, { label: string; bgColor: string; textColor: string; borderColor: string; accentColor: string }> = {
  client: { label: 'Client', bgColor: 'bg-blue-50', textColor: 'text-blue-700', borderColor: 'border-blue-200', accentColor: 'bg-blue-500' },
  shipper: { label: 'Shipper', bgColor: 'bg-green-50', textColor: 'text-green-700', borderColor: 'border-green-200', accentColor: 'bg-green-500' },
  warehouse: { label: 'Warehouse', bgColor: 'bg-orange-50', textColor: 'text-orange-700', borderColor: 'border-orange-200', accentColor: 'bg-orange-500' },
  port: { label: 'Port', bgColor: 'bg-purple-50', textColor: 'text-purple-700', borderColor: 'border-purple-200', accentColor: 'bg-purple-500' },
}

// ── ContactCard ──
interface ContactCardProps {
  title: string
  icon: React.ReactNode
  partyType?: PartyType
  name?: string
  contactPerson?: string
  phone?: string
  email?: string
  address?: string
  onEmailClick?: (email: string) => void
}

const ContactCard: React.FC<ContactCardProps> = ({
  title,
  icon,
  partyType,
  name,
  contactPerson,
  phone,
  email,
  address,
  onEmailClick,
}) => {
  const typeConfig = partyType ? PARTY_TYPE_CONFIG[partyType] : null

  // Inline row helper
  const InfoRow: React.FC<{
    icon: React.ReactNode; label: string; value?: string;
    isLink?: 'tel' | 'mailto'; isLast?: boolean
  }> = ({ icon, label, value, isLink, isLast }) => (
    <div className={`flex items-center gap-2.5 py-2 ${!isLast ? 'border-b border-gray-100' : ''}`}>
      <span className="text-gray-400 flex-shrink-0">{icon}</span>
      <span className="text-[13px] text-gray-500 w-[56px] flex-shrink-0">{label}</span>
      {value && isLink === 'tel' ? (
        <a href={`tel:${value}`} className="text-sm text-blue-600 hover:underline truncate flex-1 min-w-0">{value}</a>
      ) : value && isLink === 'mailto' ? (
        <a
          href="#"
          onClick={(e) => { e.preventDefault(); onEmailClick ? onEmailClick(value) : window.open(`/dispatch-console/email?compose=true&to=${encodeURIComponent(value)}`, '_blank') }}
          className="text-sm text-blue-600 hover:underline truncate flex-1 min-w-0"
        >{value}</a>
      ) : (
        <span className={`text-sm truncate flex-1 min-w-0 ${value ? 'text-gray-800' : 'text-gray-400'}`}>{value || '--'}</span>
      )}
    </div>
  )

  return (
    <div className="relative bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden transition-all hover:shadow-md group">
      {/* Left accent bar */}
      <div className={`absolute left-0 top-0 bottom-0 w-1 ${typeConfig ? typeConfig.accentColor : 'bg-gray-300'}`} />

      {/* Header */}
      <div className="pl-4 pr-3 py-2.5 flex items-center justify-between border-b border-gray-100">
        <div className="flex items-center gap-1.5">
          <span className={typeConfig ? typeConfig.textColor : 'text-gray-600'}>{icon}</span>
          <span className={`text-sm font-semibold ${typeConfig ? typeConfig.textColor : 'text-gray-700'}`}>{title}</span>
        </div>
        {typeConfig && (
          <span className={`text-xs px-2 py-0.5 rounded font-medium ${typeConfig.bgColor} ${typeConfig.textColor}`}>
            {typeConfig.label}
          </span>
        )}
      </div>

      {/* Company name highlight */}
      <div className="pl-4 pr-3 py-2.5 bg-gray-50/50">
        <div className="flex items-center gap-2">
          <Building2 className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <span className={`text-sm font-medium truncate ${name ? 'text-gray-900' : 'text-gray-400'}`}>{name || '--'}</span>
        </div>
      </div>

      {/* Detail rows */}
      <div className="pl-4 pr-3 pb-2.5">
        <InfoRow icon={<User className="w-4 h-4" />} label="Contact" value={contactPerson} />
        <InfoRow icon={<Phone className="w-4 h-4" />} label="Phone" value={phone} isLink="tel" />
        <InfoRow icon={<Mail className="w-4 h-4" />} label="Email" value={email} isLink="mailto" />
        <InfoRow icon={<Navigation className="w-4 h-4" />} label="Address" value={address} isLast />
      </div>
    </div>
  )
}

// ── Mini Task Card ──
const MiniTaskCard: React.FC<{
  task: ContainerTask
  index: number
  isCurrent?: boolean
  onClick?: () => void
}> = ({ task, index, isCurrent, onClick }) => {
  const cfg = taskStatusConfig[task.status]
  const taskType = task.taskType?.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || '--'

  // Simple route: origin → destination (prefer short names, resolve by origin/dest type)
  const resolveShortName = (type?: string, name?: string) => {
    if (type === 'port') return task.portShortName || task.portName || name || ''
    if (type === 'warehouse') return task.warehouseShortName || task.warehouseName || name || ''
    if (type === 'yard') return task.yardShortName || task.yardName || name || ''
    return name || ''
  }
  const origin = resolveShortName(task.originType, task.originName)
  const dest = resolveShortName(task.destinationType, task.destinationName)

  // Driver name — 'Unassigned' is the placeholder backendTaskToContainer writes
  // when no driver is on the task, so treat it as "no driver" visually.
  const driverName = task.driver && task.driver !== 'Unassigned' ? task.driver : ''

  // 跑完的那一程，这一格放「哪天跑完的」而不是 View —— 那条腿已经没什么可操作
  // 的了，调度要的是时间。用板子那列同一个 helper，不另算一份（完成日是服务端
  // 折算好的日历日，自己解析必踩时区）。
  const done = completedMarker(task)

  return (
    <div
      onClick={isCurrent ? undefined : onClick}
      className={`w-full rounded-lg p-2.5 transition-all text-left ${
        isCurrent
          ? 'bg-indigo-50 border-2 border-indigo-300 ring-1 ring-indigo-100 cursor-default'
          : 'bg-white border border-gray-100 hover:shadow-sm hover:border-gray-200 cursor-pointer'
      }`}
    >
      {/* Row 1: Index + Task ID + Task Type + Driver */}
      <div className="flex items-center gap-2 mb-1">
        <span className={`w-5 h-5 rounded-full text-[11px] font-bold flex items-center justify-center flex-shrink-0 ${
          isCurrent ? 'bg-indigo-500 text-white' : 'bg-indigo-100 text-indigo-600'
        }`}>
          {index}
        </span>
        <span className={`text-xs font-semibold ${isCurrent ? 'text-indigo-900' : 'text-gray-800'}`}>{task.taskId || task.id}</span>
        <span className={`text-[11px] font-medium ${isCurrent ? 'text-indigo-500' : 'text-gray-400'}`}>{taskType}</span>
        <span className="flex-1" />
        <span
          className={`flex items-center gap-1 text-[11px] min-w-0 ${
            driverName
              ? (isCurrent ? 'text-indigo-700' : 'text-gray-600')
              : 'text-gray-400'
          }`}
          title={driverName || 'Unassigned'}
        >
          <User className={`w-3 h-3 flex-shrink-0 ${isCurrent ? 'text-indigo-400' : 'text-gray-400'}`} />
          <span className="truncate max-w-[88px]">{driverName || 'Unassigned'}</span>
        </span>
      </div>
      {/* Row 2: Badges (Current + Status) */}
      <div className="flex items-center gap-1.5 ml-7 mb-1">
        {isCurrent && (
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-500 text-white font-semibold uppercase tracking-wider">Current</span>
        )}
        {cfg && (
          <span className={`text-[11px] px-1.5 py-0.5 rounded font-medium ${cfg.bgColor} ${cfg.textColor}`}>
            {cfg.label}
          </span>
        )}
      </div>
      {/* Row 3: Route info (origin → dest) + View */}
      <div className="flex items-center gap-2 ml-7">
        {(origin || dest) && (
          <div className={`flex items-center gap-1 text-[11px] min-w-0 flex-1 ${isCurrent ? 'text-indigo-600' : 'text-gray-500'}`}>
            <MapPin className={`w-3 h-3 flex-shrink-0 ${isCurrent ? 'text-indigo-400' : 'text-gray-400'}`} />
            <span className="truncate">{origin || '--'}</span>
            <span className={`flex-shrink-0 ${isCurrent ? 'text-indigo-300' : 'text-gray-300'}`}>→</span>
            <span className="truncate">{dest || '--'}</span>
          </div>
        )}
        {done ? (
          <span
            className={`ml-auto flex items-center gap-1 text-[11px] flex-shrink-0 ${
              isCurrent ? 'text-indigo-600' : 'text-gray-500'
            }`}
            title={done.title}
          >
            <CheckCircle2 className="w-3 h-3 flex-shrink-0 text-emerald-500" />
            {done.label}
          </span>
        ) : !isCurrent ? (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onClick?.() }}
            className="ml-auto flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-medium text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 transition-colors cursor-pointer flex-shrink-0"
          >
            <ExternalLink className="w-3 h-3" />
            View
          </button>
        ) : null}
      </div>
    </div>
  )
}

// ── Main Component ──
interface PartiesRelatedTabProps {
  container: ContainerTask
  allContainerTasks: ContainerTask[]
  onTaskClick?: (task: ContainerTask) => void
}

export const PartiesRelatedTab: React.FC<PartiesRelatedTabProps> = ({
  container,
  allContainerTasks,
  onTaskClick,
}) => {
  const navigate = useNavigate()

  // Shipper info — supports polymorphic shipper type
  const shipperInfo = {
    name: container.shipperName || container.shipper || '',
    contact: container.shipperResolvedContact?.name || container.shipperContact?.name || '',
    phone: container.shipperResolvedContact?.phone || container.shipperContact?.phone || '',
    email: container.shipperResolvedContact?.email || container.shipperContact?.email || '',
    address: container.shipperResolvedContact?.address || container.shipperContact?.address || '',
  }

  // Build email compose URL with order context pre-filled
  const handleEmailClick = (toEmail: string) => {
    const c = container
    const taskType = c.taskType?.replace(/-/g, ' ').replace(/\b\w/g, (ch: string) => ch.toUpperCase()) || ''
    const subject = `[${c.orderDisplayId || ''}] ${c.containerNumber || ''} - ${taskType}`.trim()

    const lines: string[] = []
    if (c.orderDisplayId) lines.push(`Order #: ${c.orderDisplayId}`)
    if (c.containerNumber) lines.push(`Container #: ${c.containerNumber}`)
    if (c.importExport === 'export') {
      if (c.bookingNumber) lines.push(`Booking #: ${c.bookingNumber}`)
    } else {
      if (c.bol) lines.push(`BOL: ${c.bol}`)
    }
    if (c.taskId) lines.push(`Task: ${c.taskId} (${taskType})`)
    if (c.importExport && c.importExport !== 'n/a') lines.push(`Type: ${c.importExport.charAt(0).toUpperCase() + c.importExport.slice(1)}`)
    if (c.shippingLine) lines.push(`Shipping Line: ${c.shippingLine}`)
    if (c.containerSize) lines.push(`Container Size: ${c.containerSize}`)
    if (c.lfd) lines.push(`LFD: ${formatLfdMedium(c.lfd, { year: true })}`)
    if (c.portName) lines.push(`Port: ${c.portName}`)
    if (c.warehouseName) lines.push(`Warehouse: ${c.warehouseName}`)

    const body = lines.length > 0 ? `\n\n--- Order Info ---\n${lines.join('\n')}\n---` : ''

    const params = new URLSearchParams({
      compose: 'true',
      to: toEmail,
      subject,
      body,
    })
    window.open(`/dispatch-console/email?${params.toString()}`, '_blank')
  }

  // Related tasks: every task on this order — asked of the server, not picked
  // out of whatever the board happens to hold.
  //
  // This used to filter `allContainerTasks`, which is the dispatch board's
  // loaded set: status-scoped and capped (finished tasks only go back the most
  // recent 200 by updated_at). A leg that finished a while ago simply isn't in
  // there, so an order's earlier legs — exactly what a dispatcher opens this
  // panel to see — went missing with no sign anything was left out.
  const [orderTasksFromApi, setOrderTasksFromApi] = useState<ContainerTask[] | null>(null)
  const [siblingsError, setSiblingsError] = useState(false)
  const requestIdRef = useRef(0)

  useEffect(() => {
    if (!container.orderId) {
      setOrderTasksFromApi(null)
      setSiblingsError(false)
      return
    }
    const requestId = ++requestIdRef.current
    setSiblingsError(false)
    taskAPI
      .getAll({ order_id: container.orderId, limit: 100 })
      .then((res) => {
        if (requestId !== requestIdRef.current) return
        setOrderTasksFromApi((res.data || []).map(backendTaskToContainer))
      })
      .catch((err) => {
        if (requestId !== requestIdRef.current) return
        // Fall back to the board's in-memory set rather than showing nothing.
        console.error('Failed to load tasks for order', container.orderId, err)
        setOrderTasksFromApi(null)
        setSiblingsError(true)
      })
  }, [container.orderId])

  const currentId = container.id || container.taskId

  // Server answer when we have one; the board's set otherwise (first paint and
  // on failure). Either way the task being edited is rendered from `container`
  // so unsaved edits stay visible.
  const sameOrderPool = orderTasksFromApi ??
    (container.orderId
      ? allContainerTasks.filter((t) => t.orderId && t.orderId === container.orderId)
      : [])

  const sameOrder = sameOrderPool.filter((t) => (t.id || t.taskId) !== currentId)

  // Build order-based index: all tasks under the same order sorted by taskId
  const orderTasks = [
    container,
    ...sameOrder,
  ].sort((a, b) => String(a.taskId || a.id).localeCompare(String(b.taskId || b.id)))
  const orderIndexMap = new Map<string, number>()
  orderTasks.forEach((t, i) => orderIndexMap.set(String(t.id || t.taskId), i + 1))

  const getTaskIndex = (t: ContainerTask) =>
    orderIndexMap.get(String(t.id || t.taskId)) || 0


  return (
    <div className="flex gap-6">
      {/* Left: Contact Cards (2x2 grid) */}
      <div className="flex-1 min-w-0">
        <h5 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-4 flex items-center gap-2">
          <div className="w-1 h-4 bg-blue-500 rounded-full"></div>
          Contact Parties
        </h5>
        <div className="grid grid-cols-2 gap-3">
          <ContactCard
            title="Ordering Party"
            icon={<User className="w-4 h-4" />}
            partyType={container.orderingPartyType}
            name={container.orderingPartyName}
            contactPerson={container.orderingPartyContact?.name}
            phone={container.orderingPartyContact?.phone}
            email={container.orderingPartyContact?.email}
            address={container.orderingPartyContact?.address}
            onEmailClick={handleEmailClick}
          />

          <ContactCard
            title="Bill To"
            icon={<DollarSign className="w-4 h-4" />}
            partyType={container.billToType}
            name={container.billToName}
            contactPerson={container.billToContact?.name}
            phone={container.billToContact?.phone}
            email={container.billToContact?.email}
            address={container.billToContact?.address}
            onEmailClick={handleEmailClick}
          />

          <ContactCard
            title="Deliver To"
            icon={<MapPin className="w-4 h-4" />}
            partyType={container.warehouseName ? 'warehouse' : container.deliverToType}
            name={container.warehouseName || container.deliverToName}
            contactPerson={container.warehouseContact?.name || container.deliverToContact?.name}
            phone={container.warehouseContact?.phone || container.warehousePhone || container.deliverToContact?.phone}
            email={container.warehouseContact?.email || container.deliverToContact?.email}
            address={container.warehouseAddress || container.deliverToContact?.address}
            onEmailClick={handleEmailClick}
          />

          <ContactCard
            title="Shipper"
            icon={<Building2 className="w-4 h-4" />}
            partyType={container.shipperType || (shipperInfo.name ? 'shipper' : undefined)}
            name={shipperInfo.name}
            contactPerson={shipperInfo.contact}
            phone={shipperInfo.phone}
            email={shipperInfo.email}
            address={shipperInfo.address}
            onEmailClick={handleEmailClick}
          />
        </div>
      </div>

      {/* Right: Related Order + Related Tasks */}
      <div className="w-[320px] flex-shrink-0 flex flex-col">
        {/* Related Order */}
        <h5 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-3 flex items-center gap-2">
          <div className="w-1 h-4 bg-emerald-500 rounded-full"></div>
          Related Order
        </h5>

        {container.orderId ? (
          <div className="relative bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden mb-4">
            <div className="absolute left-0 top-0 bottom-0 w-1 bg-emerald-500" />
            <div className="pl-4 pr-3 py-2.5 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-emerald-600" />
                <span className="text-sm font-semibold text-gray-900">{container.orderDisplayId || `#${container.orderId}`}</span>
              </div>
              {container.orderStatus && (
                <span className={`text-xs px-2 py-0.5 rounded font-medium ${
                  container.orderStatus === 'completed' ? 'bg-green-50 text-green-700' :
                  container.orderStatus === 'in-progress' ? 'bg-blue-50 text-blue-700' :
                  container.orderStatus === 'cancelled' ? 'bg-red-50 text-red-700' :
                  'bg-gray-100 text-gray-600'
                }`}>
                  {container.orderStatus.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                </span>
              )}
            </div>
            <div className="pl-4 pr-3 py-1.5">
              <div className="flex items-center gap-2.5 py-1.5 border-b border-gray-100">
                <Package className="w-4 h-4 text-gray-400" />
                <span className="text-[13px] text-gray-500 w-[56px] flex-shrink-0">Type</span>
                <span className={`text-sm ${container.orderType ? 'text-gray-800' : 'text-gray-400'}`}>
                  {container.orderType ? container.orderType.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '--'}
                </span>
              </div>
              <div className="flex items-center gap-2.5 py-1.5">
                <Calendar className="w-4 h-4 text-gray-400" />
                <span className="text-[13px] text-gray-500 w-[56px] flex-shrink-0">Created</span>
                <span className={`text-sm ${container.orderCreatedAt ? 'text-gray-800' : 'text-gray-400'}`}>
                  {container.orderCreatedAt ? new Date(container.orderCreatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '--'}
                </span>
              </div>
            </div>
            {/* View Order button */}
            <div className="pl-4 pr-3 py-2 border-t border-gray-100">
              <button
                onClick={() => navigate(`/dispatch-console/order-dispatch?viewOrder=${container.orderDisplayId || container.orderId}`)}
                className="w-full flex items-center justify-center gap-1.5 py-1.5 text-sm font-medium text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 rounded-md transition-colors cursor-pointer"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                View Order
              </button>
            </div>
          </div>
        ) : (
          <div className="bg-gray-50 border border-gray-100 rounded-lg p-4 text-center mb-4">
            <p className="text-sm text-gray-400">No linked order</p>
          </div>
        )}

        {/* Related Tasks */}
        <div className="flex-1 flex flex-col min-h-0">
          <h5 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-3 flex items-center gap-2">
            <div className="w-1 h-4 bg-indigo-500 rounded-full"></div>
            Related Tasks
          </h5>

          <div className="flex-1 bg-gray-50/50 border border-gray-100 rounded-lg p-3 space-y-1.5 overflow-y-auto">
            {/* The order's legs in their own sequence — a finished first leg
                belongs above the one running now, not under it. */}
            {orderTasks.map((t) =>
              (t.id || t.taskId) === currentId ? (
                <MiniTaskCard key={currentId} task={container} index={getTaskIndex(container)} isCurrent />
              ) : (
                <MiniTaskCard key={t.id || t.taskId} task={t} index={getTaskIndex(t)} onClick={() => onTaskClick?.(t)} />
              )
            )}

            {siblingsError && (
              <p className="text-[11px] text-amber-600 pt-1">
                Couldn't load this order's other tasks — showing what's on the board.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
